import React, { useState, useEffect } from 'react';
import Button from '../../lib/button/Button';
import {
	EntityWithValueState,
	LiteralValueState,
	ChangeSet,
	ProjectState,
	getParentCanvasEntity,
	StateMutationAction
} from '@elyx-code/project-logic-tree';
import { DraggableObject } from '../../lib/canvas';
import {
	DynamicValueTypes,
	IValueStoreClient
} from '../../services/editor/value-store';
import { EditorService } from '../../services/editor';
import { handleLiteralValueChange } from '../../services/execution/value-resolution';
import ElyxMonacoEditor from '../editor/ElyxMonacoEditor';

export interface IStringEditorDialogProps {
	onClose: (event: React.MouseEvent | null) => void;
	valueOwner: EntityWithValueState;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	draggableObject?: DraggableObject;
	project: EditorService;
	onlyDefault?: boolean;
}

const StringEditorDialog: React.FC<IStringEditorDialogProps> = (props) => {
	const onlyDefault = props.onlyDefault || false;
	const valueOwnerCanvasParentEntity = getParentCanvasEntity(
		props.valueOwner
	);

	const initialValue =
		props.value?.rawString ??
		(props.value?.value ? String(props.value.value) : '');
	const [rawValue, setRawValue] = useState<string>(initialValue);

	const handleEditorWillMount = (monaco: any) => {
		// Register a custom language for highlighting string escapes
		if (
			!monaco.languages
				.getLanguages()
				.some((lang: any) => lang.id === 'elyx-string')
		) {
			monaco.languages.register({ id: 'elyx-string' });
			monaco.languages.setMonarchTokensProvider('elyx-string', {
				tokenizer: {
					root: [
						[/\\n|\\t|\\r|\\\\/, 'escape-sequence'],
						[/./, '']
					]
				}
			});
			monaco.editor.defineTheme('elyx-string-theme', {
				base: 'vs',
				inherit: true,
				rules: [
					{
						token: 'escape-sequence',
						foreground: 'FFB86C',
						fontStyle: 'bold'
					}
				],
				colors: {}
			});
		}
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				padding: '40px'
			}}
		>
			{/* Header */}
			<span>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						className="dialog_back_button"
						onClick={(e) => {
							props.onClose?.(e);
						}}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px'
						}}
					>
						Edit string value
					</h1>
				</div>
				<span
					style={{ display: 'flex', gap: '6px', marginLeft: '42px' }}
				>
					<p style={{ margin: '0px', color: 'rgba(0,0,0,0.7)' }}>
						Press Enter for a new line. Escape sequences like{' '}
						<b style={{ color: '#FFB86C' }}>\n</b> will be
						highlighted.
					</p>
				</span>
			</span>

			<div
				style={{
					flex: 1,
					margin: '16px 0',
					overflow: 'scroll'
				}}
			>
				<section
					style={{
						display: 'flex',
						flexDirection: 'column',
						height: '100%'
					}}
				>
					<div
						style={{
							margin: '0 42px',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'flex-end'
						}}
					>
						<span
							style={{
								display: 'flex',
								paddingTop: 6,
								gap: 12
							}}
						>
							<span>
								<span
									style={{
										display: 'flex',
										gap: '10px',
										alignItems: 'center'
									}}
								>
									<h3
										style={{
											display: 'flex',
											gap: '6px',
											fontWeight: 'normal',
											margin: '0px'
										}}
									>
										Value
									</h3>
								</span>
								<p
									style={{
										margin: '0px',
										fontSize: '14px',
										marginTop: '6px',
										color: 'rgba(0,0,0,0.54)',
										marginBottom: '16px'
									}}
								>
									String
								</p>
							</span>
						</span>
					</div>

					<div
						style={{
							flex: 1,
							borderRadius: '4px',
							overflow: 'hidden',
							border: '1px solid var(--gray-lighter)',
							padding: '8px 0' // small padding to separate from borders
						}}
					>
						<ElyxMonacoEditor
							value={rawValue}
							onChange={(val) => setRawValue(val || '')}
							language="elyx-string"
							theme="elyx-string-theme"
							beforeMount={handleEditorWillMount}
						/>
					</div>
				</section>
			</div>

			<section
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '24px',
					paddingTop: '20px'
				}}
			>
				<Button variant="text" onClick={(e: any) => props.onClose?.(e)}>
					BACK
				</Button>

				<Button
					onClick={(e: any) => {
						let storage: ChangeSet | IValueStoreClient =
							props.project.localTestValues;

						if (onlyDefault) {
							storage = props.project.logic.addChangeSet(
								new ChangeSet(
									props.project.logic,
									ProjectState.sessionAuthor,
									new Date().toISOString(),
									props.valueOwner,
									false,
									StateMutationAction.SetPrimitiveDefaultValue
								)
							);
						}

						const resolvedValue = rawValue.replace(
							/\\(n|t|r|\\)/g,
							(match, char) => {
								switch (char) {
									case 'n':
										return '\n';
									case 't':
										return '\t';
									case 'r':
										return '\r';
									case '\\':
										return '\\';
									default:
										return match;
								}
							}
						);

						const changeSet = handleLiteralValueChange(
							resolvedValue as any,
							props.valueOwner,
							props.project.logic,
							storage,
							{ rawString: rawValue }
						);

						if (!!changeSet) {
							props.project.renderAndCloseChangeSet(changeSet);
						}

						props.project.emit(valueOwnerCanvasParentEntity.id, {});
						props.project.test?.validate();
						props.onClose?.(e);
					}}
					variant="contained"
					color="primary"
				>
					SAVE VALUE
				</Button>
			</section>
		</div>
	);
};

export default StringEditorDialog;
